import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import {
  useIsHomeHealthListRefetchPaused,
  useRegisterHomeHealthListQuery,
} from "../HomeHealthListRefetchGuard";
import {
  type Case,
  type CaseFilter,
  type CaseSort,
  type CasesParams,
  casesParamsSchema,
  casesResponseSchema,
} from "../types";
import { getCaseQueryKey } from "./useCase";
import { getPatientQueryKey } from "./usePatient";

interface AgencyCasesQueryOptions extends UseQueryOptions<Case[], unknown, Case[]> {
  pauseRefetchWhileActionDialogOpen?: boolean;
}

export function getAgencyCasesQueryKey(params: {
  workplaceId: string;
  filter?: CaseFilter;
  sort?: CaseSort;
}) {
  const keyWithFilter = [
    "home-health",
    params.workplaceId,
    "cases",
    params.filter?.status,
    { ...params.filter },
  ];

  if (isDefined(params.sort)) {
    keyWithFilter.push(params.sort);
  }

  return keyWithFilter;
}

export function useAgencyCases(params: CasesParams, options: AgencyCasesQueryOptions = {}) {
  const { filter, sort } = params;
  const { pauseRefetchWhileActionDialogOpen = false, ...queryOptions } = options;

  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";
  const isListRefetchPaused = useIsHomeHealthListRefetchPaused();
  const isQueryPaused = pauseRefetchWhileActionDialogOpen && isListRefetchPaused;
  const queryKey = getAgencyCasesQueryKey({ workplaceId, filter, sort });
  useRegisterHomeHealthListQuery({ isGuarded: pauseRefetchWhileActionDialogOpen, queryKey });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/${workplaceId}/cases`,
        requestSchema: casesParamsSchema,
        responseSchema: casesResponseSchema,
        queryParams: { filter, sort },
      });

      return response.data.data;
    },
    onSuccess: (cases) => {
      cases.forEach((caseItem) => {
        queryClient.setQueryData(getCaseQueryKey({ workplaceId, caseId: caseItem.id }), caseItem);
        queryClient.setQueryData(
          getPatientQueryKey({ workplaceId, patientId: caseItem.attributes.patient.id }),
          caseItem.attributes.patient
        );
      });
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    ...queryOptions,
    enabled: !isQueryPaused && queryOptions.enabled,
    refetchInterval: isQueryPaused ? false : (queryOptions.refetchInterval ?? 1000 * 60 * 10),
    refetchOnWindowFocus: isQueryPaused ? false : queryOptions.refetchOnWindowFocus,
  });
}
